import React, { useState, useEffect, useRef } from 'react';

interface Child {
  id: string;
  name: string;
  avatar: string;
  color: string;
  year: string;
}

interface Activity {
  id: string;
  childId: string;
  type: 'achievement' | 'alert' | 'progress' | 'streak' | 'struggle' | 'homework' | 'mastery';
  title: string;
  message: string;
  time: string;
  timestamp: number;
  icon: string;
  actionLabel?: string;
  isNew?: boolean;
}

interface ActivityFeedModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ActivityFeedModal: React.FC<ActivityFeedModalProps> = ({ isOpen, onClose }) => {
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [showNewActivity, setShowNewActivity] = useState(false);
  const feedRef = useRef<HTMLDivElement>(null);

  const children: Child[] = [
    { id: 'emma', name: 'Emma', avatar: 'E', color: '#ec4899', year: 'Year 4' },
    { id: 'jack', name: 'Jack', avatar: 'J', color: '#3b82f6', year: 'Year 2' },
    { id: 'sophie', name: 'Sophie', avatar: 'S', color: '#10b981', year: 'Year 6' },
  ];

  const allActivities: Activity[] = [
    {
      id: '1',
      childId: 'emma',
      type: 'achievement',
      title: 'Badge Earned!',
      message: 'Emma earned the "Math Whiz" badge for mastering multiplication tables',
      time: '2 minutes ago',
      timestamp: Date.now() - 2 * 60 * 1000,
      icon: '🏆',
      isNew: true,
    },
    {
      id: '2',
      childId: 'jack',
      type: 'alert',
      title: 'Homework Overdue',
      message: 'Jack\'s Fractions worksheet was due yesterday and hasn\'t been completed',
      time: '15 minutes ago',
      timestamp: Date.now() - 15 * 60 * 1000,
      icon: '⚠️',
      actionLabel: 'Send Reminder',
      isNew: true,
    },
    {
      id: '3',
      childId: 'sophie',
      type: 'mastery',
      title: 'Mastery Achieved',
      message: 'Sophie has reached 90% mastery in Year 6 Algebra concepts',
      time: '30 minutes ago',
      timestamp: Date.now() - 30 * 60 * 1000,
      icon: '⭐',
    },
    {
      id: '4',
      childId: 'emma',
      type: 'progress',
      title: 'Module Completed',
      message: 'Emma completed the Year 4 English Grammar module with 87% accuracy',
      time: '1 hour ago',
      timestamp: Date.now() - 60 * 60 * 1000,
      icon: '✓',
    },
    {
      id: '5',
      childId: 'jack',
      type: 'streak',
      title: 'Learning Streak!',
      message: 'Jack has maintained a 7-day learning streak. Keep it up!',
      time: '2 hours ago',
      timestamp: Date.now() - 2 * 60 * 60 * 1000,
      icon: '🔥',
    },
    {
      id: '6',
      childId: 'sophie',
      type: 'struggle',
      title: 'Support Suggested',
      message: 'Sophie may benefit from extra practice with quadratic equations',
      time: '3 hours ago',
      timestamp: Date.now() - 3 * 60 * 60 * 1000,
      icon: '💡',
      actionLabel: 'View Resources',
    },
    {
      id: '7',
      childId: 'emma',
      type: 'homework',
      title: 'Homework Submitted',
      message: 'Emma submitted her Science assignment on time',
      time: '4 hours ago',
      timestamp: Date.now() - 4 * 60 * 60 * 1000,
      icon: '📝',
    },
    {
      id: '8',
      childId: 'jack',
      type: 'progress',
      title: 'Lesson Completed',
      message: 'Jack finished "Introduction to Telling Time" with 92% accuracy',
      time: '5 hours ago',
      timestamp: Date.now() - 5 * 60 * 60 * 1000,
      icon: '✓',
    },
    {
      id: '9',
      childId: 'sophie',
      type: 'achievement',
      title: 'Top of Class!',
      message: 'Sophie ranked #1 in her class for weekly Science quiz',
      time: 'Yesterday',
      timestamp: Date.now() - 24 * 60 * 60 * 1000,
      icon: '🥇',
    },
    {
      id: '10',
      childId: 'emma',
      type: 'alert',
      title: 'Mastery Decay Warning',
      message: 'Emma hasn\'t practiced Fractions in 2 weeks. Review recommended.',
      time: 'Yesterday',
      timestamp: Date.now() - 26 * 60 * 60 * 1000,
      icon: '📉',
      actionLabel: 'Schedule Review',
    },
  ];

  // Initialize activities
  useEffect(() => {
    if (isOpen) {
      setActivities(allActivities);
    }
  }, [isOpen]);

  // Simulate new activity arriving
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setShowNewActivity(true);
      setTimeout(() => {
        const newActivity: Activity = {
          id: `new-${Date.now()}`,
          childId: children[Math.floor(Math.random() * children.length)].id,
          type: 'progress',
          title: 'Just Now',
          message: 'Completed a practice session',
          time: 'Just now',
          timestamp: Date.now(),
          icon: '✨',
          isNew: true,
        };
        setActivities(prev => [newActivity, ...prev]);
        setShowNewActivity(false);
      }, 1000);
    }, 8000);

    return () => clearInterval(interval);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const filteredActivities = selectedChild
    ? activities.filter(a => a.childId === selectedChild)
    : activities;

  const getChild = (childId: string) => children.find(c => c.id === childId);

  const getTypeStyles = (type: Activity['type']) => {
    switch (type) {
      case 'achievement':
        return { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.2)', accent: '#f59e0b' };
      case 'alert':
        return { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)', accent: '#ef4444' };
      case 'progress':
        return { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.2)', accent: '#10b981' };
      case 'streak':
        return { bg: 'rgba(249, 115, 22, 0.1)', border: 'rgba(249, 115, 22, 0.2)', accent: '#f97316' };
      case 'struggle':
        return { bg: 'rgba(139, 92, 246, 0.1)', border: 'rgba(139, 92, 246, 0.2)', accent: '#8b5cf6' };
      case 'homework':
        return { bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.2)', accent: '#3b82f6' };
      case 'mastery':
        return { bg: 'rgba(6, 182, 212, 0.1)', border: 'rgba(6, 182, 212, 0.2)', accent: '#06b6d4' };
      default:
        return { bg: 'rgba(255, 255, 255, 0.05)', border: 'rgba(255, 255, 255, 0.1)', accent: '#94a3b8' };
    }
  };

  const alertCount = activities.filter(a => a.type === 'alert').length;
  const todayCount = activities.filter(a => a.timestamp > Date.now() - 24 * 60 * 60 * 1000).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
        >
          <div>
            <h2 className="text-xl font-bold" style={{ color: '#e2e8f0' }}>
              Family Activity Feed
            </h2>
            <p className="text-sm mt-1" style={{ color: '#94a3b8' }}>
              Real-time updates from all your children's learning
            </p>
          </div>
          <div className="flex items-center gap-4">
            {alertCount > 0 && (
              <div
                className="px-3 py-1.5 rounded-lg flex items-center gap-2"
                style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}
              >
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#ef4444' }} />
                <span className="text-sm font-medium" style={{ color: '#ef4444' }}>
                  {alertCount} alert{alertCount > 1 ? 's' : ''} need attention
                </span>
              </div>
            )}
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(255, 255, 255, 0.05)' }}
            >
              <svg className="w-5 h-5" style={{ color: '#94a3b8' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex" style={{ height: 'calc(90vh - 140px)' }}>
          {/* Sidebar - Children */}
          <div
            className="w-64 p-4 border-r overflow-auto"
            style={{ borderColor: 'rgba(255, 255, 255, 0.1)', background: 'rgba(0, 0, 0, 0.2)' }}
          >
            <div className="text-xs font-semibold mb-3" style={{ color: '#64748b' }}>
              YOUR CHILDREN
            </div>

            {/* All filter */}
            <button
              onClick={() => setSelectedChild(null)}
              className="w-full p-3 rounded-lg mb-2 flex items-center gap-3 transition-all"
              style={{
                background: selectedChild === null ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                border: selectedChild === null ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent',
              }}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(255, 255, 255, 0.1)' }}
              >
                <svg className="w-5 h-5" style={{ color: '#94a3b8' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="text-left">
                <div className="text-sm font-medium" style={{ color: '#e2e8f0' }}>All Children</div>
                <div className="text-xs" style={{ color: '#64748b' }}>{todayCount} updates today</div>
              </div>
            </button>

            {/* Individual children */}
            {children.map((child) => {
              const childActivities = activities.filter(a => a.childId === child.id);
              const childAlerts = childActivities.filter(a => a.type === 'alert').length;

              return (
                <button
                  key={child.id}
                  onClick={() => setSelectedChild(child.id)}
                  className="w-full p-3 rounded-lg mb-2 flex items-center gap-3 transition-all"
                  style={{
                    background: selectedChild === child.id ? `${child.color}15` : 'transparent',
                    border: selectedChild === child.id ? `1px solid ${child.color}40` : '1px solid transparent',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold relative"
                    style={{ background: child.color }}
                  >
                    {child.avatar}
                    {childAlerts > 0 && (
                      <div
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold"
                        style={{ background: '#ef4444', color: 'white' }}
                      >
                        {childAlerts}
                      </div>
                    )}
                  </div>
                  <div className="text-left flex-1">
                    <div className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{child.name}</div>
                    <div className="text-xs" style={{ color: '#64748b' }}>{child.year}</div>
                  </div>
                </button>
              );
            })}

            {/* Quick stats */}
            <div className="mt-6 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color: '#64748b' }}>
                TODAY'S SUMMARY
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: '#94a3b8' }}>Achievements</span>
                  <span className="text-xs font-bold" style={{ color: '#f59e0b' }}>
                    {activities.filter(a => a.type === 'achievement').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: '#94a3b8' }}>Completed</span>
                  <span className="text-xs font-bold" style={{ color: '#10b981' }}>
                    {activities.filter(a => a.type === 'progress' || a.type === 'homework').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: '#94a3b8' }}>Need Attention</span>
                  <span className="text-xs font-bold" style={{ color: '#ef4444' }}>
                    {activities.filter(a => a.type === 'alert' || a.type === 'struggle').length}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Feed */}
          <div className="flex-1 overflow-auto p-6" ref={feedRef}>
            {/* New activity indicator */}
            {showNewActivity && (
              <div
                className="mb-4 p-3 rounded-lg text-center animate-pulse"
                style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
              >
                <span className="text-sm" style={{ color: '#3b82f6' }}>
                  New activity incoming...
                </span>
              </div>
            )}

            {/* Activity items */}
            <div className="space-y-4">
              {filteredActivities.map((activity, index) => {
                const child = getChild(activity.childId);
                const styles = getTypeStyles(activity.type);

                return (
                  <div
                    key={activity.id}
                    className="p-4 rounded-xl transition-all duration-300"
                    style={{
                      background: styles.bg,
                      border: `1px solid ${styles.border}`,
                      animation: activity.isNew && index === 0 ? 'fadeIn 0.5s ease' : 'none',
                    }}
                  >
                    <div className="flex items-start gap-4">
                      {/* Child avatar */}
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold shrink-0"
                        style={{ background: child?.color }}
                      >
                        {child?.avatar}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold" style={{ color: child?.color }}>
                            {child?.name}
                          </span>
                          <span className="text-xs" style={{ color: '#64748b' }}>
                            • {activity.time}
                          </span>
                          {activity.isNew && (
                            <span
                              className="px-2 py-0.5 rounded text-[10px] font-bold"
                              style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6' }}
                            >
                              NEW
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 mb-2">
                          <span style={{ fontSize: '18px' }}>{activity.icon}</span>
                          <span className="font-semibold" style={{ color: '#e2e8f0' }}>
                            {activity.title}
                          </span>
                        </div>

                        <p className="text-sm" style={{ color: '#94a3b8' }}>
                          {activity.message}
                        </p>

                        {activity.actionLabel && (
                          <button
                            className="mt-3 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80"
                            style={{ background: styles.accent, color: 'white' }}
                          >
                            {activity.actionLabel}
                          </button>
                        )}
                      </div>

                      {/* Type indicator */}
                      <div
                        className="px-2 py-1 rounded text-[10px] font-medium shrink-0"
                        style={{ background: `${styles.accent}20`, color: styles.accent }}
                      >
                        {activity.type.charAt(0).toUpperCase() + activity.type.slice(1)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right sidebar - Notifications settings */}
          <div
            className="w-72 p-4 border-l overflow-auto"
            style={{ borderColor: 'rgba(255, 255, 255, 0.1)', background: 'rgba(0, 0, 0, 0.2)' }}
          >
            <div className="text-xs font-semibold mb-3" style={{ color: '#64748b' }}>
              NOTIFICATION PREFERENCES
            </div>

            <div className="space-y-3">
              {[
                { label: 'Achievements', desc: 'Badges and milestones', enabled: true, color: '#f59e0b' },
                { label: 'Alerts', desc: 'Overdue & struggles', enabled: true, color: '#ef4444' },
                { label: 'Progress', desc: 'Completed lessons', enabled: true, color: '#10b981' },
                { label: 'Streaks', desc: 'Learning consistency', enabled: false, color: '#f97316' },
                { label: 'Mastery', desc: 'Skill achievements', enabled: true, color: '#06b6d4' },
              ].map((pref) => (
                <div
                  key={pref.label}
                  className="p-3 rounded-lg"
                  style={{ background: 'rgba(255, 255, 255, 0.02)' }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: pref.color }} />
                      <span className="text-sm font-medium" style={{ color: '#e2e8f0' }}>
                        {pref.label}
                      </span>
                    </div>
                    <div
                      className="w-8 h-4 rounded-full relative cursor-pointer transition-all"
                      style={{ background: pref.enabled ? pref.color : 'rgba(255,255,255,0.1)' }}
                    >
                      <div
                        className="w-3 h-3 rounded-full absolute top-0.5 transition-all"
                        style={{
                          background: 'white',
                          left: pref.enabled ? '18px' : '2px',
                        }}
                      />
                    </div>
                  </div>
                  <p className="text-[10px]" style={{ color: '#64748b' }}>
                    {pref.desc}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color: '#64748b' }}>
                DELIVERY METHOD
              </div>
              <div className="space-y-2">
                {['Push notifications', 'Email digest', 'SMS for alerts'].map((method, i) => (
                  <div
                    key={method}
                    className="flex items-center gap-2 p-2 rounded-lg"
                    style={{ background: 'rgba(255, 255, 255, 0.02)' }}
                  >
                    <div
                      className="w-4 h-4 rounded border-2 flex items-center justify-center"
                      style={{
                        borderColor: i < 2 ? '#3b82f6' : '#475569',
                        background: i < 2 ? '#3b82f6' : 'transparent',
                      }}
                    >
                      {i < 2 && (
                        <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      )}
                    </div>
                    <span className="text-xs" style={{ color: '#94a3b8' }}>{method}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 border-t flex items-center justify-between"
          style={{ borderColor: 'rgba(255, 255, 255, 0.1)', background: 'rgba(0, 0, 0, 0.2)' }}
        >
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" style={{ color: '#10b981' }} viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
              <path d="M12 6v6l4 2" stroke="white" strokeWidth="2" fill="none" />
            </svg>
            <span className="text-sm" style={{ color: '#94a3b8' }}>
              Updates refresh automatically every few seconds
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{ background: 'rgba(255, 255, 255, 0.05)', color: '#94a3b8' }}
            >
              Mark All Read
            </button>
            <button
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                color: 'white',
              }}
            >
              View Full Reports
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ActivityFeedModal;
